import React, {useEffect, useState} from 'react';
import Page from '../../Components/Page';
import SelectDropdown from '../../Components/Dropdowns/SelectDropdown';
import {makeApiCall, makeApiCallPost} from '../../../Api/makeApiCall';
import ReactTable from "../../Components/ReactTable";
import _ from "lodash";
import FileLink from "../../IngestionLogs/Columns/FileLink";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import NameValueLabels from "../../Components/NameValueLabels";
import csvDownloadWithNulls from "../../../utils/CsvDownloadWithNulls";
import {createDagRunConf} from "../../../Api/dagRunConf";
import ButtonLoader from "../../../utils/components/ButtonLoader";
import {Breadcrumbs} from "../../Components/Breadcrumbs";

const ProjectionsSpotlightPage = () => {
    const [selectedVersion1, setSelectedVersion1] = useState(undefined);
    const [selectedVersion2, setSelectedVersion2] = useState(undefined);
    const [extractOptions, setExtractOptions] = useState([]);
    const [previousSpotlights, setPreviousSpotlights] = useState([]);

    const [loadingPreviousSpotlights, setLoadingPreviousSpotlights] = useState(true);
    const [loadingProjectionsSpotlight, setLoadingProjectionsSpotlight] = useState(false);

    const [selectedProjectionSpotlight, setSelectedProjectionSpotlight] = useState(undefined);
    const [projectionFactorsSummary, setProjectionFactorsSummary] = useState([]);
    const [storesUniverseSummary, setStoresUniverseSummary] = useState([]);

    useEffect(() => {
        setLoadingPreviousSpotlights(true);
        Promise.all([
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/listDirectories/?bucket=spins-edp-${process.env.REACT_APP_ENV}-checkpoint&subdirectory=p_dataset=projection_qc_extract`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/projectionsSpotlightMetadata/`)
        ])
        .then(([
            projectionCheckpointResults,
            previousSpotlightsResults
        ]) => {
            const cleanedProjectionCheckpointResults = (projectionCheckpointResults || [])
                .map((value) => {
                    const run_id = value.replace('p_run_id=', '');
                    const checkpointPathPrefix = `gs://spins-edp-${process.env.REACT_APP_ENV}-checkpoint/p_dataset=projection_qc_extract/p_run_id=${run_id}/`
                    return ({label: run_id, value: checkpointPathPrefix});
                })
            setExtractOptions(cleanedProjectionCheckpointResults);
            setPreviousSpotlights(previousSpotlightsResults);
        }).finally(() => setLoadingPreviousSpotlights(false));
    }, []);

    const isDisabled = !(selectedVersion1 && selectedVersion2);

    const getSummaryApiEndpoint = (row, tableType) => `${process.env.REACT_APP_EDP_API_BASE_URL}/projectionsSpotlight/?run_id=${row.original.run_identifier}&table_type=${tableType}`;

    const getSummaryData = (row) => {
        setSelectedProjectionSpotlight(row.original);
        const tablesInSpotlight = row.original.tables || [];
        setLoadingProjectionsSpotlight(true);

        const calls = [];
        if (tablesInSpotlight.includes('stores_universe')) {
            calls.push(
                makeApiCall(getSummaryApiEndpoint(row, "stores_universe"))
                    .then((response) => setStoresUniverseSummary(response?.data))
            );
        }
        if (tablesInSpotlight.includes('projection_factors')) {
            calls.push(
                makeApiCall(getSummaryApiEndpoint(row, "projection_factors"))
                    .then((response) => setProjectionFactorsSummary(response?.data))
            );
        }

        Promise.all(calls).finally(() => setLoadingProjectionsSpotlight(false));
    }

    const downloadCsv = (rows, tableType) => {
        const data = rows.map(x => x.original);
        csvDownloadWithNulls(data, `${tableType}_summary.csv`, 0)
    }

    const showPreviousRuns = () => {
        setProjectionFactorsSummary([]);
        setStoresUniverseSummary([]);
        setSelectedProjectionSpotlight(undefined);
    }

    const previousSpotlightsColumns = [
        {header: "Version 1", accessorKey: "to_this_table", size: 200,
            cell: ({row}) => FileLink({
                gcsFileLocation: row.original.to_this_table,
                displayText: row.original.to_run_id
            })},
        {header: "Version 2", accessorKey: "from_this_table", size: 200,
            cell: ({row}) => FileLink({
                gcsFileLocation: row.original.from_this_table,
                displayText: row.original.from_run_id
            })},
        {header: "Extract Tables", accessorKey: "tables"},
        {header: "Date Ran", accessorKey: "created_date"},
        {header: "Triggered By", accessorKey: "triggered_by"},
        {
            header: 'View Comparison',
            cell: ({row}) => <button className={"button secondary"}
                                     disabled={loadingProjectionsSpotlight}
                                     onClick={() => getSummaryData(row)}>View</button>
        }
    ];

    const projectionFactorsSummaryColumns = [
        {header: "Universe", accessorKey: "universe"},
        {header: "Market Id", accessorKey: "marketId"},
        {header: "Cell Id", accessorKey: "cellId"},
        {header: "Source", accessorKey: "source"},
        {header: "Total Weeks", accessorKey: "totalWeeks"},
        {header: "Acv Diffs", accessorKey: "acvDiffs"},
        {header: "Stratum Acv Diffs", accessorKey: "stratumAcvDiffs"},
        {header: "In Sample Acv Diffs", accessorKey: "inSampleAcvDiffs"},
        {header: "Projection Factor Diffs", accessorKey: "projectionFactorDiffs"},
    ];

    const storesUniverseSummaryColumns = [
        {header: "Universe", accessorKey: "universe"},
        {header: "Source", accessorKey: "source"},
        {header: "Cell Id", accessorKey: "cellId"},
        {header: "Total Stores", accessorKey: "totalStores"},
        {header: "Cell Market Diffs", accessorKey: "cellMarketIdDiffs"},
        {header: "Cell Universe Diffs", accessorKey: "cellUniverseDiffs"}
    ];

    return (
        <Page name={"Spotlight: Projections"} beta={true}
              breadCrumbs={<Breadcrumbs items={[{label: "Services"}, {label: "Projections"}, {label: "Spotlight: Projections"}]}/>}
        >
            <h5>Compare Projection Versions</h5>
            <div className={'spotlight-selection-group projections-spotlight'}>
                <div className={'column'} data-testid={'projection-versions-section'}>
                    <div className={'version-selection'}>
                        <div className={'version-selection-row'}>
                            <div className={'version-field'} data-testid={'version-1-dropdown-div'}>
                                <SelectDropdown
                                    id={"version_1_dropdown"}
                                    dataTestId={"version-1-dropdown"}
                                    inputLabel={"Version 1"}
                                    options={extractOptions}
                                    selectValue={selectedVersion1}
                                    selectLabel={extractOptions?.find(option => option.value === selectedVersion1)?.label}
                                    onChange={(option) => setSelectedVersion1(option?.value)}
                                    placeholder={"Select..."}
                                />
                            </div>
                            <div className={'version-field'} data-testid={'version-2-dropdown-div'}>
                                <SelectDropdown
                                    id={"version_2_dropdown"}
                                    dataTestId={"version-2-dropdown"}
                                    inputLabel={"Version 2"}
                                    options={extractOptions}
                                    selectValue={selectedVersion2}
                                    selectLabel={extractOptions?.find(option => option.value === selectedVersion2)?.label}
                                    onChange={(option) => setSelectedVersion2(option.value)}
                                    placeholder={"Select..."}
                                />
                            </div>
                            <div className={'submit-spotlight'} data-testid={'submit-spotlight-container'}>
                                <ButtonLoader buttonTitle={"Generate Comparison"}
                                              buttonClassName={"button submit left-margin"}
                                              axiosFunction={makeApiCallPost}
                                              postBody={{
                                                  dagName: "projections_spotlight_pre_process",
                                                  data: createDagRunConf({
                                                      to_this_table: selectedVersion1,
                                                      from_this_table: selectedVersion2
                                                  })
                                              }}
                                              disabled={isDisabled}
                                              url={`${process.env.REACT_APP_EDP_API_BASE_URL}/dagTrigger`}
                                              toastTextFailure={`Generate Comparison has failed to kicked off.`}
                                              toastTextSuccess={`Generate Comparison has successfully kicked off.`}
                                />
                            </div>
                            <div className="show-previous-runs">
                                <button className="button submit" onClick={showPreviousRuns}>Previous Spotlights</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {(_.isEmpty(projectionFactorsSummary) && _.isEmpty(storesUniverseSummary) )&& <div data-testid={'previous-spotlights-container'}>
                <LoadingWrapper loading={loadingPreviousSpotlights}>
                    <div>
                        <h5>Previous Spotlights</h5>
                        <ReactTable
                            filterable={true}
                            columns={previousSpotlightsColumns}
                            data={previousSpotlights}
                            initialState={{pagination: {pageSize: 10}, sorting: [{id: "created_date", desc: true}]}}
                        />
                    </div>
                </LoadingWrapper>
            </div>}
            {(!_.isEmpty(selectedProjectionSpotlight)) && <LoadingWrapper loading={loadingProjectionsSpotlight}>
                <div className="projections-spotlights-summary-header">
                    <h5>Projection Summary</h5>
                    <NameValueLabels labelValuePairs={[
                        {label: "Version 1:", value: selectedProjectionSpotlight.to_run_id},
                        {label: "Version 2:", value: selectedProjectionSpotlight.from_run_id},
                        {label: "Tables:", value: selectedProjectionSpotlight.tables?.join(', ')}
                    ]}/>
                </div>
                {!_.isEmpty(projectionFactorsSummary) && <div data-testid={'projection-factors-summary-container'}>
                    <h5>Projection Factors Summary</h5>
                    <ReactTable
                        filterable={true}
                        columns={projectionFactorsSummaryColumns}
                        data={projectionFactorsSummary}
                    >
                        {({rows}) => {
                            return <div className={"projection-factors-summary-table"}>
                                {rows.length > 0 &&
                                    <div className={"tab-download-csv"}>
                                        <button className={'download-spotlight-csv hollow button'}
                                                onClick={() => downloadCsv(rows, 'projection_factors')}>{'Download CSV'}</button>
                                    </div>
                                }
                            </div>
                        }}
                    </ReactTable>
                </div>}
                {!_.isEmpty(storesUniverseSummary) && <div data-testid={'stores-universe-summary-container'}>
                    <h5>Stores Universe Summary</h5>
                    <ReactTable
                        filterable={true}
                        columns={storesUniverseSummaryColumns}
                        data={storesUniverseSummary}
                    >
                        {({rows}) => {
                            return <div className={"stores-universe-summary-table"}>
                                {rows.length > 0 &&
                                    <div className={"tab-download-csv"}>
                                        <button className={'download-spotlight-csv hollow button'}
                                                onClick={() => downloadCsv(rows, 'stores_universe')}>{'Download CSV'}</button>
                                    </div>
                                }
                            </div>
                        }}
                    </ReactTable>
                </div>}
            </LoadingWrapper>}
        </Page>
    );
};

export default ProjectionsSpotlightPage;
